import { useState, useEffect, useRef } from 'react'
import { FloppyDisk, Bicycle, CheckCircle, XCircle, Star, Info } from '@phosphor-icons/react'
import { supabase } from '../lib/supabase'

async function saveDataToSupabase(projectId, data) {
  try {
    const { data: { user }, error: authErr } = await supabase.auth.getUser()
    if (authErr || !user) return
    const { error } = await supabase.from('evaluations').upsert({
      project_id: projectId,
      user_id: user.id,
      category: 'Transporte',
      criteria_code: 'TRA3-DATA',
      criteria_name: 'TRA3 Datos',
      points_obtained: 0, points_max: 0, cumple: false,
      justificacion: JSON.stringify(data),
      updated_at: new Date().toISOString(),
    }, { onConflict: 'project_id,category,criteria_code' })
    if (error) console.error('saveData error:', error.message)
  } catch (e) { console.error('saveData exception:', e.message) }
}

async function loadDataFromSupabase(projectId) {
  try {
    const { data } = await supabase.from('evaluations')
      .select('justificacion').eq('project_id', projectId)
      .eq('criteria_code', 'TRA3-DATA').single()
    if (data?.justificacion) return JSON.parse(data.justificacion)
  } catch (e) {}
  return null
}

// ── Opciones TRA 3 según BREEAM ES NC 2015 (páginas 166-167)
const OPTIONS = [
  {
    id: 1,
    label: 'Opción 1 — Red ciclista con autoridades locales',
    hint: 'Acuerdo con autoridad local para desarrollo de red ciclista',
    criterios: [
      { key: 'c1', label: 'Acuerdo/compromiso formal con la autoridad local para la creación o mejora de la red ciclista que conecte el edificio con destinos principales.' },
      { key: 'c2', label: 'Existe o está previsto un itinerario ciclista seguro y continuo desde el edificio hacia al menos un destino principal (trabajo, transporte público, comercio…).' },
    ],
  },
  {
    id: 2,
    label: 'Opción 2 — Mejora del servicio de autobús',
    hint: 'Acuerdo con operador de autobús + IA ≥ 1,00',
    criterios: [
      { key: 'c3', label: 'Existe un compromiso formal con el operador de transporte para mejorar la frecuencia, el horario o la ruta del servicio de autobús que da servicio al edificio.' },
      { key: 'c4', label: 'El Índice de Accesibilidad (IA) resultante de la mejora acordada es ≥ 1,00 según la Calculadora TRA 1.' },
    ],
    extraField: {
      key: 'ia_mejora',
      label: 'IA resultante tras la mejora del servicio',
      placeholder: 'ej. 1.25',
      min: 0,
      threshold: 1.0,
      unit: '',
    },
  },
  {
    id: 3,
    label: 'Opción 3 — Puntos de recarga para VE',
    hint: '≥ 3 % de plazas con cargador o preinstalación VE',
    criterios: [
      { key: 'c5', label: 'Al menos el 3 % de las plazas de aparcamiento están equipadas o pre-equipadas (canalización + panel) con puntos de recarga para vehículos eléctricos (VE), conforme al RD 1053/2014 o normativa vigente.' },
    ],
    extraField: {
      key: 'pct_ve',
      label: '% de plazas con cargador o preinstalación VE',
      placeholder: 'ej. 5',
      min: 0,
      threshold: 3,
      unit: '%',
    },
  },
  {
    id: 4,
    label: 'Opción 4 — Car sharing',
    hint: 'Plan de vehículo compartido con plazas designadas',
    criterios: [
      { key: 'c6', label: 'Existe un plan formal de uso compartido de vehículo (car sharing) para los usuarios del edificio.' },
      { key: 'c7', label: 'Se reservan plazas de aparcamiento designadas exclusivamente para los vehículos de car sharing.' },
      { key: 'c8', label: 'Existe un protocolo/acuerdo de gestión del servicio de car sharing (operador, tarifas, reservas…).' },
      { key: 'c9', label: 'Se comunica activamente el servicio de car sharing a todos los usuarios del edificio mediante señalización, intranet u otros canales.' },
    ],
  },
  {
    id: 5,
    label: 'Opción 5 — Aparcamiento de bicicletas',
    hint: 'Plazas suficientes + vestuarios + taquillas + plano',
    criterios: [
      { key: 'c10', label: 'Las plazas de aparcabicicletas para personal/empleados son suficientes según la calculadora (mínimo reglamentario BREEAM).' },
      { key: 'c11', label: 'Se proveen plazas de aparcabicicletas para visitantes/clientes/pacientes (si aplica según el tipo de edificio).' },
      { key: 'c12', label: 'Se habilitan vestuarios con duchas para ciclistas (>10 empleados): al menos 1 ducha + 1 taquilla por cada 10 ciclistas esperados.' },
      { key: 'c13', label: 'Se proveen taquillas/armarios seguros para guardar casco, ropa y accesorios de los ciclistas.' },
      { key: 'c14', label: 'Se aporta plano de situación del aparcabicicletas y los vestuarios dentro del edificio/parcela.' },
    ],
  },
]

const INP_STYLE = {
  padding: '0.3rem 0.45rem', fontSize: '0.72rem',
  border: '1px solid #C8D8C0', borderRadius: '4px',
  background: 'white', fontFamily: "'IBM Plex Mono', monospace",
  boxSizing: 'border-box', color: '#2C2017',
}

// ── Calculadora mínimo plazas bicicleta (BREEAM ES NC 2015)
function calcMinBikePlaces(nUsuarios) {
  const n = parseFloat(nUsuarios)
  if (!n || n <= 0) return 0
  const tramo1 = Math.min(n, 500) * 0.10
  const tramo2 = Math.max(0, Math.min(n, 1000) - 500) * 0.07
  const tramo3 = Math.max(0, n - 1000) * 0.05
  return Math.ceil(tramo1 + tramo2 + tramo3)
}

// ── Fila de criterio — DEFINIDA FUERA del componente para evitar remontado
function CriterioRow({ cKey, label, checked, onChange }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: '0.6rem',
      padding: '0.4rem 0.5rem', borderRadius: '4px',
      background: checked ? '#EAF3EC' : 'transparent',
      marginBottom: '0.25rem',
    }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(cKey, e.target.checked)}
        style={{ width: '14px', height: '14px', cursor: 'pointer', accentColor: '#2E4A38', flexShrink: 0, marginTop: '1px' }}
      />
      <div style={{ fontSize: '0.68rem', color: '#2C2017', lineHeight: '1.4' }}>{label}</div>
      <div style={{ width: '16px', flexShrink: 0 }}>
        {checked && <CheckCircle size={14} weight="fill" style={{ color: '#3B7A50', marginTop: '1px' }} />}
      </div>
    </div>
  )
}

function initState() {
  const criterios = {}
  OPTIONS.forEach(opt => opt.criterios.forEach(({ key }) => { criterios[key] = false }))
  return {
    criterios,
    nUsuarios: '',      // para calculadora bicicleta (Opción 5)
    nVisitantes: '',    // visitantes por día (Opción 5, c11)
    ia_mejora: '',      // IA resultante mejora autobús (Opción 2)
    pct_ve: '',         // % plazas VE (Opción 3)
  }
}

function calcPts(state) {
  const { criterios, nUsuarios, ia_mejora, pct_ve } = state

  // Evaluar si cada opción está cumplida
  const opt1OK = criterios.c1 && criterios.c2
  const opt2OK = criterios.c3 && criterios.c4 && parseFloat(ia_mejora) >= 1.0
  const opt3OK = criterios.c5 && parseFloat(pct_ve) >= 3
  const opt4OK = criterios.c6 && criterios.c7 && criterios.c8 && criterios.c9
  // Opción 5: c10 requiere que haya plazas suficientes
  const minPlazas = calcMinBikePlaces(nUsuarios)
  const opt5OK = criterios.c10 && criterios.c11 && criterios.c12 && criterios.c13 && criterios.c14

  const fulfilled = [opt1OK, opt2OK, opt3OK, opt4OK, opt5OK].filter(Boolean).length
  const ptsStd = fulfilled >= 1 ? 2 : 0
  const ptsExtra = fulfilled >= 2 ? 1 : 0

  return { pts: ptsStd + ptsExtra, ptsStd, ptsExtra, fulfilled, opt1OK, opt2OK, opt3OK, opt4OK, opt5OK, minPlazas }
}

export default function TRA3Calculator({ projectId, onSave, saving }) {
  const storageKey = `tra3_${projectId}`
  const [savedOk, setSavedOk] = useState(false)
  const [state, setState] = useState(() => {
    if (!projectId) return initState()
    try {
      const saved = JSON.parse(localStorage.getItem(`tra3_${projectId}`) || 'null')
      return saved || initState()
    } catch { return initState() }
  })

  // Ref-guard: bloquea el persist hasta que el load de Supabase termine (BUG-race).
  const hasLoaded = useRef(false)

  // Cargar desde Supabase al montar
  useEffect(() => {
    if (!projectId) return
    loadDataFromSupabase(projectId).then(remote => {
      if (remote) setState(remote)
      hasLoaded.current = true
    })
  }, [projectId]) // eslint-disable-line

  // Persistir: localStorage inmediato + Supabase debounced 1.5s (BUG-009)
  const supabaseSaveTimer = useRef(null)
  useEffect(() => {
    if (!projectId) return
    if (!hasLoaded.current) return
    localStorage.setItem(storageKey, JSON.stringify(state))
    if (supabaseSaveTimer.current) clearTimeout(supabaseSaveTimer.current)
    supabaseSaveTimer.current = setTimeout(() => saveDataToSupabase(projectId, state), 1500)
    return () => { if (supabaseSaveTimer.current) clearTimeout(supabaseSaveTimer.current) }
  }, [state, projectId]) // eslint-disable-line

  // Flush al desmontar
  useEffect(() => {
    return () => {
      if (!projectId) return
      if (!hasLoaded.current) return
      localStorage.setItem(storageKey, JSON.stringify(state))
      if (supabaseSaveTimer.current) {
        clearTimeout(supabaseSaveTimer.current)
        saveDataToSupabase(projectId, state)
      }
    }
  }, [projectId, state]) // eslint-disable-line

  function saveNow() {
    localStorage.setItem(storageKey, JSON.stringify(state))
    saveDataToSupabase(projectId, state)
    setSavedOk(true)
    setTimeout(() => setSavedOk(false), 2000)
  }

  const updateCriterio = (key, val) =>
    setState(prev => ({ ...prev, criterios: { ...prev.criterios, [key]: val } }))

  const updateField = (field, val) =>
    setState(prev => ({ ...prev, [field]: val }))

  const result = calcPts(state)
  const { pts, ptsStd, ptsExtra, fulfilled, opt1OK, opt2OK, opt3OK, opt4OK, opt5OK, minPlazas } = result
  const optStatus = [opt1OK, opt2OK, opt3OK, opt4OK, opt5OK]

  const labelStyle = {
    fontSize: '0.58rem', fontWeight: '700', color: '#5A7A4A',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    display: 'block', marginBottom: '0.25rem',
  }

  return (
    <div style={{ marginBottom: '1.25rem', border: '2px solid #6A9A7A', borderRadius: '6px', overflow: 'hidden' }}>

      {/* Cabecera */}
      <div style={{ background: '#2E4A38', padding: '0.6rem 1rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
        <Bicycle size={15} weight="fill" style={{ color: '#A8D0B8', flexShrink: 0 }} />
        <span style={{ fontSize: '0.65rem', fontWeight: '700', color: '#A8D0B8', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
          Calculadora BREEAM ES TRA 3 — Modos de Transporte Alternativos
        </span>
      </div>

      <div style={{ padding: '0.875rem', background: '#F5FAF6' }}>

        {/* Nota de puntuación */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', background: '#EEF4EA', border: '1px solid #B0D0B8', borderRadius: '5px', padding: '0.5rem 0.7rem', marginBottom: '1rem' }}>
          <Info size={13} style={{ color: '#3B7A50', flexShrink: 0, marginTop: '1px' }} />
          <div style={{ fontSize: '0.62rem', color: '#2E4A38', lineHeight: '1.45' }}>
            <strong>Puntuación:</strong> Se obtienen <strong>2 puntos</strong> si el proyecto cumple al menos 1 opción.
            Si se cumplen <strong>≥ 2 opciones</strong>, se suma 1 punto extraordinario → <strong>máximo 3 puntos</strong> en total.
          </div>
        </div>

        {/* ─── Las 5 opciones ─── */}
        {OPTIONS.map((opt, idx) => {
          const isOK = optStatus[idx]
          const allKeys = opt.criterios.map(c => c.key)
          const checkedCount = allKeys.filter(k => state.criterios[k]).length

          return (
            <div key={opt.id} style={{
              marginBottom: '0.75rem',
              border: `1px solid ${isOK ? '#6A9A7A' : '#DDD5C8'}`,
              borderRadius: '6px', overflow: 'hidden',
            }}>
              {/* Cabecera opción */}
              <div style={{
                background: isOK ? '#2E4A38' : '#F0EBE3',
                padding: '0.45rem 0.7rem',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem',
              }}>
                <div>
                  <span style={{ fontSize: '0.65rem', fontWeight: '700', color: isOK ? '#A8D0B8' : '#5A4A3A', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {opt.label}
                  </span>
                  <span style={{ fontSize: '0.58rem', color: isOK ? '#8FC8A0' : '#9A8A7A', marginLeft: '0.5rem' }}>
                    — {opt.hint}
                  </span>
                </div>
                <span style={{ fontSize: '0.62rem', fontWeight: '700', color: isOK ? '#A8D0B8' : '#B0A89E', fontFamily: "'IBM Plex Mono', monospace", flexShrink: 0 }}>
                  {isOK ? '✓ cumple' : `${checkedCount}/${allKeys.length}`}
                </span>
              </div>

              <div style={{ padding: '0.6rem 0.7rem', background: 'white' }}>
                {opt.criterios.map(({ key, label }) => (
                  <CriterioRow
                    key={key}
                    cKey={key}
                    label={label}
                    checked={state.criterios[key]}
                    onChange={updateCriterio}
                  />
                ))}

                {/* Campo extra numérico (Opciones 2 y 3) */}
                {opt.extraField && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.4rem', paddingTop: '0.4rem', borderTop: '1px dashed #D8E8D0' }}>
                    <label style={{ ...labelStyle, margin: 0, fontSize: '0.6rem' }}>{opt.extraField.label}</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                      <input
                        type="number"
                        min={opt.extraField.min}
                        step="0.01"
                        value={state[opt.extraField.key]}
                        placeholder={opt.extraField.placeholder}
                        onChange={e => updateField(opt.extraField.key, e.target.value)}
                        style={{ ...INP_STYLE, width: '100px',
                          borderColor: parseFloat(state[opt.extraField.key]) >= opt.extraField.threshold ? '#6A9A7A' : '#C8D8C0' }}
                      />
                      {opt.extraField.unit && <span style={{ fontSize: '0.65rem', color: '#7A6A5A' }}>{opt.extraField.unit}</span>}
                      <span style={{ fontSize: '0.58rem', color: '#9A8A7A' }}>
                        (umbral: ≥ {opt.extraField.threshold}{opt.extraField.unit})
                      </span>
                      {parseFloat(state[opt.extraField.key]) >= opt.extraField.threshold
                        ? <CheckCircle size={13} weight="fill" style={{ color: '#3B7A50' }} />
                        : parseFloat(state[opt.extraField.key]) > 0
                          ? <XCircle size={13} weight="fill" style={{ color: '#D4614A' }} />
                          : null}
                    </div>
                  </div>
                )}

                {/* Calculadora mínimo plazas bicicleta (Opción 5) */}
                {opt.id === 5 && (
                  <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.6rem', background: '#F5FAF6', border: '1px solid #D0E8D8', borderRadius: '4px' }}>
                    <div style={{ fontSize: '0.6rem', fontWeight: '700', color: '#3B5A47', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Calculadora de plazas mínimas de aparcabicicletas
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
                      <div>
                        <label style={{ ...labelStyle, fontSize: '0.58rem' }}>Nº usuarios/empleados habituales</label>
                        <input
                          type="number" min="0" step="1"
                          value={state.nUsuarios}
                          placeholder="ej. 150"
                          onChange={e => updateField('nUsuarios', e.target.value)}
                          style={{ ...INP_STYLE, width: '110px' }}
                        />
                      </div>
                      <div>
                        <label style={{ ...labelStyle, fontSize: '0.58rem' }}>Nº visitantes/clientes/día (si aplica)</label>
                        <input
                          type="number" min="0" step="1"
                          value={state.nVisitantes}
                          placeholder="ej. 50"
                          onChange={e => updateField('nVisitantes', e.target.value)}
                          style={{ ...INP_STYLE, width: '110px' }}
                        />
                      </div>
                      {state.nUsuarios && (
                        <div style={{ background: '#EAF3EC', border: '1px solid #6A9A7A', borderRadius: '4px', padding: '0.35rem 0.75rem', textAlign: 'center' }}>
                          <div style={{ fontSize: '0.55rem', color: '#3B5A47', fontWeight: '700', textTransform: 'uppercase' }}>Plazas personal mínimas</div>
                          <div style={{ fontSize: '1.3rem', fontWeight: '800', color: '#1A3020', fontFamily: "'IBM Plex Mono', monospace", lineHeight: 1 }}>
                            {minPlazas}
                          </div>
                          <div style={{ fontSize: '0.52rem', color: '#6A9A7A' }}>
                            10% × 500 + 7% × 500 + 5% × resto
                          </div>
                        </div>
                      )}
                    </div>
                    {state.nUsuarios && (
                      <div style={{ fontSize: '0.58rem', color: '#7A6A5A', marginTop: '0.3rem' }}>
                        Con {state.nUsuarios} usuarios → mínimo <strong>{minPlazas} plazas</strong> para personal.
                        {state.nVisitantes ? ` Visitantes: se recomienda ≈ ${Math.ceil(parseFloat(state.nVisitantes) * 0.05)} plazas adicionales (5% visitantes).` : ''}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        })}

        {/* ─── Resultados ─── */}
        <div style={{ background: 'white', border: '1px solid #B8D4C0', borderRadius: '6px', padding: '0.875rem' }}>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.875rem' }}>

            {/* Puntos estándar */}
            <div style={{ background: '#EAF3EC', border: '2px solid #2E4A38', borderRadius: '6px', padding: '0.5rem 1.25rem', textAlign: 'center', minWidth: '100px' }}>
              <div style={{ fontSize: '0.55rem', fontWeight: '700', color: '#2E4A38', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.2rem' }}>Puntos TRA 3</div>
              <div style={{ fontSize: '1.6rem', fontWeight: '800', color: '#1A3020', fontFamily: "'IBM Plex Mono', monospace", lineHeight: 1 }}>
                {pts}<span style={{ fontSize: '0.8rem', color: '#6A9A7A' }}>/3</span>
              </div>
            </div>

            {/* Detalle */}
            <div style={{ flex: 1, minWidth: '160px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.3rem' }}>
                {ptsStd > 0
                  ? <CheckCircle size={14} weight="fill" style={{ color: '#3B7A50' }} />
                  : <XCircle    size={14} weight="fill" style={{ color: '#B0A89E' }} />}
                <span style={{ fontSize: '0.68rem', color: ptsStd > 0 ? '#2E4A38' : '#9A8A7A', fontWeight: ptsStd > 0 ? '600' : '400' }}>
                  {ptsStd > 0 ? `${fulfilled} opción${fulfilled > 1 ? 'es' : ''} cumplida${fulfilled > 1 ? 's' : ''} → 2 pts estándar` : 'Ninguna opción cumplida aún → 0 pts'}
                </span>
              </div>
              {fulfilled >= 2 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Star size={14} weight="fill" style={{ color: '#C98A2E' }} />
                  <span style={{ fontSize: '0.68rem', color: '#7A5A1A', fontWeight: '700' }}>
                    + 1 punto extraordinario (≥ 2 opciones) → total 3 pts
                  </span>
                </div>
              )}
              {fulfilled === 1 && (
                <div style={{ fontSize: '0.62rem', color: '#9A8A7A', marginTop: '0.15rem' }}>
                  Cumple 1 opción más para obtener el punto extraordinario.
                </div>
              )}
              {fulfilled === 0 && (
                <div style={{ fontSize: '0.62rem', color: '#9A8A7A', marginTop: '0.15rem' }}>
                  Cumple al menos 1 opción completa para obtener 2 pts.
                </div>
              )}
            </div>

            {/* Resumen opciones */}
            <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
              {OPTIONS.map((opt, idx) => (
                <div key={opt.id} style={{
                  fontSize: '0.58rem', fontWeight: '700', padding: '0.2rem 0.45rem', borderRadius: '3px',
                  background: optStatus[idx] ? '#2E4A38' : '#EDE8E2',
                  color: optStatus[idx] ? '#A8D0B8' : '#9A8A7A',
                  fontFamily: "'IBM Plex Mono', monospace",
                }}>
                  Op.{opt.id} {optStatus[idx] ? '✓' : '–'}
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', alignItems: 'center' }}>
            <button onClick={saveNow} style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.35rem 0.75rem', background: 'transparent',
              color: savedOk ? '#8D9A2E' : '#7A6A5A',
              border: `1px solid ${savedOk ? '#8D9A2E' : '#DDD5C8'}`,
              borderRadius: '5px', cursor: 'pointer', fontSize: '0.78rem',
              fontFamily: "'DM Sans', sans-serif"
            }}>
              <FloppyDisk size={14} />{savedOk ? '✓ Guardado' : 'Guardar datos'}
            </button>
            <button
              onClick={() => onSave({ pts, ptsStd, ptsExtra, fulfilled, state })}
              disabled={saving}
              style={{ background: '#3B2F2F', color: '#F5F0EA', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.775rem', fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '0.4rem', fontFamily: "'DM Sans', sans-serif" }}
            >
              <FloppyDisk size={14} />
              {saving ? 'Guardando…' : `Guardar puntuación TRA 3 (${pts} pt${pts !== 1 ? 's' : ''})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
